
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { auth, analytics, functions, db } from '../../../firebase';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import ResetPasswordDialog from '../../../components/auth/ResetPasswordDialog';
import MfaDialog from '../../../components/auth/MfaDialog';
import InfoTooltip from '../../../components/InfoTooltip';
import useEmailPasswordSignIn from '../../../hooks/auth/useEmailPasswordSignIn';
import MicrosoftButton from '../../../components/auth/MicrosoftButton';
import { buildMicrosoftProvider, isProjectAllowedToUseMicrosoftSSO } from '../../../utils/auth/microsoftProvider';
import { signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { sanitizeEmailInput } from '../../../utils/inputSanitization';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import TermsPrivacyConsent from '../../../components/TermsPrivacyConsent';

export default function ClinicianSignUp() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  // Track Microsoft SSO flow state independently of email/password loading
  const [msLoading, setMsLoading] = useState(false);

  const handleForgotPasswordClose = () => setShowForgotPassword(false);

  // MFA dialog state; actual MFA SMS and assertion handled via shared hook
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  // MFA code handled entirely inside MfaDialog; pass through to hook directly

  // Sign-up has moved to ProviderSignUp.js; this page is sign-in only

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [supportCtx, setSupportCtx] = useState({ userId: '', organisation: '' });

  // Centralized sign-in flow (persistence, email verification, temp password reroute, MFA, errors)
  const { 
    isLoading: hookLoading, 
    error: hookError, 
    errorCode: hookErrorCode, 
    mfaState, 
    startSignIn, 
    // startGoogleSignIn, 
    completeMfa, 
    resetRecaptcha, 
    cancelMfa, 
    resendMfa 
  } = useEmailPasswordSignIn({
    entryAccountType: 'Provider',
    onRoute: (route, payload) => {
      if (route === 'verifyEmail') return navigate('/verify-email');
      if (route === 'passwordSetup') {
        const encodedEmail = encodeURIComponent(btoa(payload.email));
        return navigate(`/set-up-password?setup=true&e=${encodedEmail}`);
      }
      if (route === 'mfaSetup') return navigate('/set-up-mfa');
      if (route === 'dashboard') return navigate('/dashboard');
    }
  });

  // Open MFA dialog when required by sign-in hook
  useEffect(() => {
    if (mfaState.required) setShowMFAPrompt(true);
  }, [mfaState.required]);

  // Handle success messages from URL parameters
  useEffect(() => {
    const message = searchParams.get('message');
    const encodedEmail = searchParams.get('e');
    const urlEmail = searchParams.get('email');
    const ssoError = searchParams.get('sso_error');
    
    if (message === 'setup_complete') {
      setSuccessMessage('Your account has been set up successfully! Please sign in with your new password.');
    } else if (message === 'account_already_setup') {
      setSuccessMessage('Your account is already set up. Please sign in with your password.');
    }

    // Email prefill is independent of the message type — it fires for any URL
    // carrying ?e= (base64) or ?email= (plain), including the new email-link flow
    // where no `message` param is present (e.g. "Account Setup Complete" email).
    // Prefer base64 param `e`; fall back to url-encoded `email`.
    if (encodedEmail) {
      try {
        const decoded = atob(encodedEmail);
        setFormData(prev => ({ ...prev, email: decoded }));
      } catch (_) {}
    } else if (urlEmail) {
      setFormData(prev => ({ ...prev, email: decodeURIComponent(urlEmail) }));
    }

    // Microsoft-first invite onboarding errors (mainly for redirect flows)
    if (ssoError) {
      const friendly = (() => {
        if (ssoError === 'invite_expired') return 'Your invite has expired. Please contact your organisation administrator.';
        if (ssoError === 'invite_not_found') return 'No active invite was found for your account. Please contact your organisation administrator.';
        if (ssoError === 'blocked_wrong_tenant') return 'You must sign in with your NHS Microsoft account for your organisation.';
        if (ssoError === 'blocked_uninvited_email') return 'Your account is not yet enabled. Please contact your organisation administrator.';
        if (ssoError === 'blocked_missing_tenant_claim') return 'We could not verify your organisation tenant. Please contact your organisation administrator.';
        return 'Sign in with Microsoft failed. Please contact your organisation administrator.';
      })();
      setError(friendly);
    }
  }, [searchParams]);

  // Gather User ID and claims for support emails
  useEffect(() => {
    const u = auth?.currentUser;
    if (!u) return;
    setSupportCtx((prev) => ({ ...prev, userId: u.uid }));
    try {
      u.getIdTokenResult(true).then((res) => {
        const org = res?.claims?.organisation || '';
        setSupportCtx({ userId: u.uid, organisation: org });
      }).catch(() => {});
    } catch (_) {}
  }, []);

  // Password input change handler (no client-side validation on sign-in)
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData(prev => ({
      ...prev,
      password: newPassword
    }));
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    await startSignIn(formData.email, formData.password);
  };

  // Only expose Microsoft SSO for the single-tenant SLAM NHS project
  const canShowMicrosoftSSO = isProjectAllowedToUseMicrosoftSSO();
  // SLaM NHS project UX policy:
  // - We want SSO-first (and effectively SSO-only) sign-in for clinicians.
  // - Email/password exists as a break-glass fallback (Support can reset),
  //   but we intentionally hide it during the pilot to avoid confusion.
  //
  // We use the presence of a tenant lock to identify the SLaM project build.
  // const isSlamTenantLockedProject = !!(
  //   import.meta.env.VITE_MS_TENANT_ID &&
  //   import.meta.env.VITE_MS_TENANT_ID.trim().length > 0
  // );
  // TEMPORARY DEBUG / OPS OVERRIDE (SLaM pilot support)
  // --------------------------------------------------
  // During the SLaM pilot it’s possible to lock yourself out of the Org Admin account
  // if the Microsoft linking flow is failing and the password UI is hidden.
  //
  // For the duration of the current live debugging session, we force the password UI
  // to remain visible so an Org Admin can sign in via email/password and manage invites.
  //
  // IMPORTANT: Revert this after the call to restore the intended SSO-first UX.
  const hidePasswordSignInUI = false; // (was: canShowMicrosoftSSO && isSlamTenantLockedProject)
  // Dev/test-only: personal Microsoft accounts sometimes fail popup due to browser COOP / extension
  // interference. Redirect flow is more robust because it does not rely on cross-window messaging.
  // Normalize env var (strip wrapping quotes) for dev convenience.
  const msTestTenantRaw = (import.meta.env.VITE_MS_TEST_TENANT || '').trim();
  const msTestTenant = msTestTenantRaw.replace(/^['"]|['"]$/g, '');
  const forceMicrosoftRedirect = msTestTenant === 'consumers';

  // Shared post-Microsoft-sign-in logic for both popup and redirect flows.
  const postMicrosoftSignIn = async (screen) => {
    // console.log('[TD-SSO] ClinicianSignUp: postMicrosoftSignIn start', {
    //   screen,
    //   uid: auth?.currentUser?.uid,
    // });
    // Ensure claims are fresh post sign-in
    const tokenResult = await auth.currentUser.getIdTokenResult(true);
    // console.log('[TD-SSO] ClinicianSignUp: claims after MS sign-in (summary)', {
    //   screen,
    //   uid: auth?.currentUser?.uid,
    //   accountType: tokenResult?.claims?.accountType,
    //   userType: tokenResult?.claims?.userType,
    //   organisation: tokenResult?.claims?.organisation,
    //   authMode: tokenResult?.claims?.authMode,
    //   hasSetOwnPassword: Boolean(tokenResult?.claims?.hasSetOwnPassword),
    //   hasCompletedSetup: Boolean(tokenResult?.claims?.hasCompletedSetup),
    // });

    // For invited Provider users who haven't completed setup yet, finalize now
    const setupComplete =
      tokenResult.claims?.hasCompletedSetup === true ||
      tokenResult.claims?.hasSetOwnPassword === true;
    if (tokenResult.claims?.accountType === 'Provider' && !setupComplete) {
      console.log('[TD-SSO] ClinicianSignUp: setup incomplete -> calling completeProviderUserSSOSetup', {
        screen,
        uid: auth?.currentUser?.uid,
        authMode: tokenResult?.claims?.authMode,
      });
      const completeSSO = httpsCallable(functions, 'completeProviderUserSSOSetup');
      await completeSSO();
      await auth.currentUser.getIdTokenResult(true);
      console.log('[TD-SSO] ClinicianSignUp: completeProviderUserSSOSetup returned (token refreshed)');
    }

    // Post-sign-in guard: require Provider claims and clinicians doc.
    // Microsoft-first invite onboarding:
    // - On first Microsoft sign-in, the Auth user exists but has no Provider claims and no clinicians doc yet.
    // - We call `consumeProviderUserInvite` to convert the invite into a real Provider account.
    let refreshed = await auth.currentUser.getIdTokenResult(true);
    let providerOk = refreshed.claims?.accountType === 'Provider';
    let clinicianDoc = await getDoc(doc(db, 'clinicians', auth.currentUser.uid));

    if (!providerOk || !clinicianDoc.exists()) {
      try {
        const consumeInvite = httpsCallable(functions, 'consumeProviderUserInvite');
        await consumeInvite();
        // Force-refresh token so newly set claims become visible client-side immediately.
        refreshed = await auth.currentUser.getIdTokenResult(true);
        providerOk = refreshed.claims?.accountType === 'Provider';
        clinicianDoc = await getDoc(doc(db, 'clinicians', auth.currentUser.uid));
      } catch (e) {
        const msg = String(e?.message || '');
        // Map common invite errors to a stable query param so redirect flows can show a message too.
        const code =
          msg.includes('invite_expired') ? 'invite_expired'
            : msg.includes('invite_not_found') ? 'invite_not_found'
              : msg.includes('invite_org_not_supported') ? 'invite_org_not_supported'
                : 'invite_not_found';
        try {
          await signOut(auth);
        } catch (_) {}
        setError('Your account is not yet enabled. Please contact your organisation administrator.');
        // Keep the user on /login and persist a machine-readable hint in the URL for future refreshes.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('sso_error', code);
          window.history.replaceState({}, '', url.toString());
        } catch (_) {}
        return;
      }
    }
    console.log('[TD-SSO] ClinicianSignUp: post-sign-in guard check', {
      screen,
      uid: auth?.currentUser?.uid,
      providerOk,
      clinicianDocExists: clinicianDoc.exists(),
      authMode: refreshed?.claims?.authMode,
    });
    if (!providerOk || !clinicianDoc.exists()) {
      // Sign out and inform user to contact admin
      await signOut(auth);
      setError('Your account is not yet enabled. Please contact your organisation administrator.');
      logEvent(analytics, 'sso_guard_signout', {
        screen,
        reason: !providerOk ? 'missing_provider_claims' : 'missing_clinicians_doc',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      });
      return;
    }

    logEvent(analytics, 'sso_microsoft_success', {
      screen,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
    });

    // Proceed to the Provider dashboard
    console.log('[TD-SSO] ClinicianSignUp: navigating to /dashboard', { screen, uid: auth?.currentUser?.uid });
    return window.location.assign('/dashboard');
  };

  // Redirect completion is handled globally in `useAuth()` so it works even when the
  // login route is wrapped by `AutoLoginRedirect` (which may show a loading spinner
  // before this page mounts). Keeping redirect completion in one place avoids
  // "double consume" issues where multiple callers race to read the redirect result.

  // Microsoft SSO sign-in handler (popup on desktop, redirect on mobile)
  // Business logic:
  // - After sign-in, refresh custom claims. If the Provider user hasn't completed setup
  //   (legacy flag hasSetOwnPassword=false), finalize via callable and refresh claims again.
  // - On success, route to dashboard. Handle account-linking error with clear guidance.
  const handleMicrosoftSignIn = async () => {
    try {
      setError('');
      setMsLoading(true);
      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Redirect sign-in is recommended on mobile and on hosted environments.
      // On localhost, redirect flows are often flaky due to browser storage/cookie restrictions,
      // so we prefer popup unless the device is mobile.
      const shouldRedirect = isMobile || (forceMicrosoftRedirect && !isLocalhost);
      // console.log('[TD-SSO] ClinicianSignUp: starting Microsoft sign-in', {
      //   hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      //   isMobile,
      //   isLocalhost,
      //   forceMicrosoftRedirect,
      //   shouldRedirect,
      //   projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      //   env: import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV,
      //   tenantLocked: Boolean(import.meta.env.VITE_MS_TENANT_ID && import.meta.env.VITE_MS_TENANT_ID.trim()),
      // });
      logEvent(analytics, 'sso_microsoft_click', { 
        screen: 'ClinicianSignIn',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        isMobile,
        flow: shouldRedirect ? 'redirect' : 'popup',
      });

      const provider = buildMicrosoftProvider();
      if (shouldRedirect) {
        // Debug marker so we can confirm a redirect was actually initiated and persisted.
        try {
          sessionStorage.setItem('__td_ms_redirect_started', String(Date.now()));
          sessionStorage.setItem('__td_ms_redirect_tenant', String(import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common'));
        } catch (_) {}
        console.log('[MicrosoftSSO] starting redirect flow', {
          shouldRedirect,
          msTestTenant,
          tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        });
        await signInWithRedirect(auth, provider);
        return;
      }

      await signInWithPopup(auth, provider);
      // console.log('[TD-SSO] ClinicianSignUp: popup sign-in resolved', {
      //   uid: auth?.currentUser?.uid,
      //   providerIds: (auth?.currentUser?.providerData || []).map(p => p?.providerId).filter(Boolean),
      //   emailDomain: (auth?.currentUser?.email || '').split('@')[1] || '',
      // });
      await postMicrosoftSignIn('ClinicianSignIn');
    } catch (e) {
      let message = 'Sign in with Microsoft failed. Please try again.';
      if (e?.code === 'auth/account-exists-with-different-credential') {
        // For tenant-locked (SLaM) builds we hide password sign-in. Give a support-forward message.
        message = hidePasswordSignInUI
          ? 'We could not link your Microsoft sign-in to your account. Please contact your organisation administrator.'
          : 'An account already exists with the same email but different sign-in method. Sign in with your email/password once, then link Microsoft from Settings.';
      }
      setError(message);
      // console.warn('[TD-SSO] ClinicianSignUp: Microsoft SSO Error (full)', {
      //   name: e?.name,
      //   code: e?.code,
      //   message: e?.message,
      //   stack: e?.stack,
      //   customData: e?.customData,
      // });
      // // Raw error object for DevTools inspection (can include extra Firebase fields).
      // console.warn('[TD-SSO] ClinicianSignUp: Microsoft SSO raw error object:', e);
      logEvent(analytics, 'sso_microsoft_error', {
        screen: 'ClinicianSignIn',
        code: e?.code || 'unknown',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        tenant: import.meta.env.VITE_MS_TENANT_ID || msTestTenant || 'common',
        flow: forceMicrosoftRedirect ? 'redirect' : 'popup',
      });
    } finally {
      setMsLoading(false);
    }
  };

  // Add cleanup when the dialog closes
  const handleMFADialogClose = (open) => {
    setShowMFAPrompt(open);
    if (!open) {
      resetRecaptcha();
    }
  };

  // Add this component at the top of your file
  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  // Ensure consistent vertical spacing at the top of the panel:
  // in dev/staging we render an environment banner that provides top space;
  // in production (no banner), we add top margin to the logo so it doesn't
  // touch the rounded top edge of the card.
  const showEnvBanner = (import.meta.env.VITE_ENVIRONMENT === "development" || import.meta.env.VITE_ENVIRONMENT === "staging");

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
      {/* Invisible reCAPTCHA container is mounted globally in public/index.html */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl drop-shadow-lg px-8 pb-8 mx-4">
                  <div className="w-full">
          {showEnvBanner && (
            <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
              ${import.meta.env.VITE_ENVIRONMENT === "development" ? "bg-yellow-200 text-yellow-900 border-yellow-300" : "bg-blue-200 text-blue-900 border-blue-300"}
            `}>
              <div className="flex items-center justify-center gap-2">                
                <span>
                  {import.meta.env.VITE_ENVIRONMENT === "development" ? "Development Environment" : "Testing / Training Environment"}
                </span>
                  </div>
                </div>
            )}
          <img src="/td_logo_full_2025.png" alt="Logo" className={`mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`} style={{ width: '220px', height: 'auto' }} />
          <h2 className="text-2xl font-bold mb-2 text-center">Provider Portal</h2>
          <p className="text-gray-600 mb-6 text-center">Please enter your details to access your account.</p>
          
          {canShowMicrosoftSSO && (
            <div className="mb-6">
              <MicrosoftButton onClick={handleMicrosoftSignIn} loading={msLoading} />
              {!hidePasswordSignInUI && (
                <div className="flex items-center my-4">
                  <div className="flex-grow h-px bg-gray-200" />
                  <span className="px-3 text-xs text-gray-500">or</span>
                  <div className="flex-grow h-px bg-gray-200" />
                </div>
              )}
            </div>
          )}
          {!hidePasswordSignInUI && (
          <form onSubmit={handleSignIn} autoComplete="on">

            {/* Always show email and password fields */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Email</label>
                <InfoTooltip 
                  content={"Use the email address your organisation registered for you. If you’re unsure, contact your organisation administrator"}
                  side="right"
                />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: sanitizeEmailInput(e.target.value) })}
                required
                // autoComplete="username"
                placeholder="Enter your email"
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
              />
            </div>
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-gray-700 mb-1 text-left">Password</label>
                <InfoTooltip 
                  content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                  side="right"
                />
              </div>
              <div className="relative">
    <input
      type={showPassword ? "text" : "password"}
      value={formData.password}
      onChange={handlePasswordChange}
      required
      // autoComplete="current-password"
      placeholder="Enter your password"
      className="w-full px-4 pr-10 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
    />
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute inset-y-0 right-0 pr-3 flex items-center"
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      {/*
        Both icons are always mounted so we can transition between them.
        They sit in a fixed-size relative wrapper; each icon is absolute so
        they occupy the same space.  We cross-fade + lightly scale using
        the same transition speed used for other interactive elements in this
        file (150 ms feels instant but still registers as a deliberate change).
      */}
      <span className="relative h-5 w-5">
        <EyeIcon
          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
            showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
        />
        <EyeOffIcon
          className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
            showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
          }`}
        />
      </span>
    </button>
  </div>
            </div>

            {/* Confirm password removed (sign-in only) */}
            <button
              type="submit"
              disabled={hookLoading}
              className={`w-full py-2 px-4 rounded-md font-semibold text-white 
                  flex items-center justify-center mb-4 
                  ${hookLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90'
                }`}
            >
              {hookLoading ? (
                <>
                  <LoadingSpinner />
                  {'Signing in...'}
                </>
              ) : (
                'Sign In'
              )}
            </button>
            <TermsPrivacyConsent />
          </form>
          )}
          {/* Separator + Google button for sign in */}
            {/* <>
              <div className="my-4">
                <div className="flex items-center">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="px-3 text-xs font-semibold text-gray-500 tracking-wide">or continue with</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </div>

              <button
                onClick={() => startGoogleSignIn(formData.email || undefined)}
                disabled={hookLoading}
                className="font-semibold flex items-center justify-center w-full border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-100 transition duration-200 mb-4 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google logo" className="w-5 h-5 mr-2" />
                Google
              </button>
            </> */}
          <div className={`transition-all duration-300 overflow-hidden ${successMessage ? 'max-h-24 mb-4' : 'max-h-0 mb-0'}`}>
            <div className={`bg-green-100 border border-green-400 text-green-700 px-4 py-3 text-sm rounded-lg relative ${successMessage ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <div className="flex items-start">
                <svg className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{successMessage}</span>
              </div>
            </div>
          </div>

          <div className={`transition-all duration-300 overflow-hidden ${error || hookError ? 'max-h-24' : 'max-h-0'}`}>
            <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error || hookError ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`} role="alert">
              <span className="block sm:inline">{error || hookError}</span>
              {hookErrorCode && (
                <span className="block text-xs mt-1 text-gray-600">Error code: {hookErrorCode}</span>
              )}
            </div>
          </div>
          
          {/* Sign up link removed; separate ProviderSignUp route now handles this */}
            {!hidePasswordSignInUI && (
            <p className="text-center mt-2">
              <button
                onClick={() => setShowForgotPassword(true)}
                className="text-blue-900 hover:text-blue-700 text-sm"
              >
                Forgot your password?
              </button>
            </p>
            )}

          <ResetPasswordDialog
            open={showForgotPassword}
            onOpenChange={handleForgotPasswordClose}
            initialEmail={formData.email}
            onSubmit={async (email) => {
              const sendProviderPasswordResetEmail = httpsCallable(functions, 'sendProviderPasswordResetEmail');
              await sendProviderPasswordResetEmail({ email });
              logEvent(analytics, 'password_reset_requested');
            }}
          />
          {/* Support footer */}
          <div className="mt-6 text-center">
            {(() => {
              const subject = 'Reporting Issue with ThinkDivergent';
              const now = new Date();
              const day = now.getDate();
              const sfx = (d) => {
                if (d % 100 >= 11 && d % 100 <= 13) return 'th';
                switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
              };
              const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
              const isoTime = now.toISOString();
              const currentError = (error || hookError || '').toString();
              const supportCode = hookErrorCode || '';
              const lines = [
                'To ThinkDivergent Support Team,',
                '',
                'I would like to report an issue on the platform.',
                '',
                '[Please describe briefly what you were doing and what happened]',
                '',
                'Helpful Information:',
                `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
                `- Time: ${easyTime} (${isoTime})`,
                supportCtx.userId ? `- User ID: ${supportCtx.userId}` : undefined,
                supportCtx.organisation ? `- Organisation: ${supportCtx.organisation}` : undefined,
                supportCode ? `- Support/Error code: ${supportCode}` : undefined,
                currentError ? `- Message: ${currentError}` : undefined,
                `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
              ].filter(v => v != null).join('\n');
              const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
              return (
                <p className="text-xs text-gray-500">
                  Need help? Contact your organisation administrator or {""}
                  <a href={href} className="underline text-primary">
                    report an issue
                  </a>
                  .
                </p>
              );
            })()}
                    </div>
        </div>
      </div>
      <MfaDialog
        open={showMFAPrompt}
        onOpenChange={handleMFADialogClose}
        mfaType={mfaState?.mfaType || 'phone'}
        maskedPhone={mfaState?.maskedPhone}
        isLoading={hookLoading}
        error={error || hookError}
        errorCode={hookErrorCode}
        sentAt={mfaState.sentAt}
        cooldownSeconds={30}
        onVerify={async (code) => {
          await completeMfa(code);
        }}
        onCancel={cancelMfa}
        onResend={async () => await resendMfa()}
        tooltipSide="right"
      />
    </div>
  );
}
